import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spin, Empty, Modal, Input, Button, App } from 'antd';
import { EnvironmentOutlined, AimOutlined, LinkOutlined, PushpinOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useEventSelection } from '../../../contexts/useEventSelection';
import { useTheme } from '../../../contexts/useTheme';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePlayerOptional } from '../usePlayerContext';

import { useMapInitialization } from '../../Nowcasting/useMapInitialization';
import { useMapLayers } from '../../Nowcasting/useMapLayers';
import { useNumericRadarLayer } from '../../Nowcasting/useNumericRadarLayer';
import { useStormTrackingLayer } from '../../Nowcasting/useStormTrackingLayer';
import { useRainPathLayer } from '../../Nowcasting/useRainPathLayer';
import { RadarLegend } from '../../Nowcasting/RadarLegend';
import { isTioProvider, type MapTileProvider, type MapBasemapThemeOverride } from '../../Nowcasting/useNowcastingState';
import { LRUImageCache, RadarPreloader, buildPreloadPriorities } from '../../Nowcasting/radarPreloader';
import { circuitsService, type CircuitWithGeoJSON } from '../../../api/circuitsService';
import radarTilesService from '../../../api/radarTilesService';
import { useStationObservations } from '../useStationObservations';
import { getEventSource, isEventActive } from '../../../utils/eventSource';
import { type TioTileField } from '../../../api/tomorrowIoTilesService';
import { NOWCAST_OFFSETS } from '../../Nowcasting/nowcastingConstants';

import type { WidgetProps } from './types';

dayjs.extend(utc);

interface RadarTile {
  timestamp: string;
  url: string;
  datetime: dayjs.Dayjs;
  isNowcast?: boolean;
  forecastOffset?: number;
}


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

function cfg<T>(config: Record<string, unknown>, key: string, fallback: T): T {
  const v = config[key];
  return v !== undefined && v !== null ? (v as T) : fallback;
}

function parseApiTile(t: { timestamp: string; url: string }): RadarTile {
  const hasOffset = t.timestamp.includes('+');
  const baseTs = hasOffset ? t.timestamp.split('+')[0] : t.timestamp;
  const offset = hasOffset ? parseInt(t.timestamp.split('+')[1], 10) : undefined;
  return {
    timestamp: t.timestamp,
    url: t.url.startsWith('/api/') ? `${API_BASE_URL}${t.url}` : t.url,
    datetime: dayjs.utc(baseTs, 'YYYYMMDDHHmmss').add(offset ?? 0, 'minute'),
    isNowcast: !!offset,
    forecastOffset: offset,
  };
}

function findClosestIndex(tiles: RadarTile[], targetMs: number): number {
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < tiles.length; i++) {
    const diff = Math.abs(tiles[i].datetime.valueOf() - targetMs);
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  }
  return bestIdx;
}

const LIVE_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const RadarMapWidget = ({
  config,
  onConfigChange,
  pixelSize,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const { theme } = useTheme();
  const { isRoot } = usePermissions();
  const player = usePlayerOptional();
  const { stations: sharedStations } = useStationObservations();
  const { message } = App.useApp();
  const selectedDateRange = player?.selectedDateRange ?? null;

  const configuredProvider = cfg<MapTileProvider>(config, 'mapTileProvider', 'radar-l2');
  const eventIsLegacy = selectedEvent ? getEventSource(selectedEvent) === 'legacy' : false;
  const mapTileProvider: MapTileProvider = eventIsLegacy
    ? 'radar'
    : configuredProvider === 'radar'
      ? 'radar-l2'
      : configuredProvider;
  const radarOpacity = cfg<number>(config, 'radarOpacity', 0.7);
  const mapThemeOverride = cfg<MapBasemapThemeOverride>(config, 'mapThemeOverride', null);
  const mapStyleKey = cfg<string | null>(config, 'mapStyle', null);
  const showWindArrows = cfg<boolean>(config, 'showWindArrows', false);
  const showStormTracking = cfg<boolean>(config, 'showStormTracking', false);
  const showRainPath = cfg<boolean>(config, 'showRainPath', false);
  const showRadiusCircles = cfg<boolean>(config, 'showRadiusCircles', false);
  const radiusCircleCount = cfg<number>(config, 'radiusCircleCount', 3);
  const radiusCircleDistance = cfg<number>(config, 'radiusCircleDistance', 5);
  const showCircleOverlay = cfg<boolean>(config, 'showCircleOverlay', false);
  const showRadarBbox = cfg<boolean>(config, 'showRadarBbox', false);
  const isRainOnlyMode = cfg<boolean>(config, 'isRainOnlyMode', false);
  const trackLineWidth = cfg<number>(config, 'trackLineWidth', 3);
  const numericNoiseFloor = cfg<number>(config, 'numericNoiseFloor', 0);
  const numericColormap = cfg<string>(config, 'numericColormap', 'marshall-palmer');
  const savedMapCenter = cfg<[number, number] | null>(config, 'mapCenter', null);
  const savedMapZoom = cfg<number | null>(config, 'mapZoom', null);
  const showZoomIndicator = cfg<boolean>(config, 'showZoomIndicator', false);
  const showLegend = cfg<boolean>(config, 'showLegend', false);
  const legendCompact = cfg<boolean>(config, 'legendCompact', false);

  const effectiveTioField: TioTileField = isRainOnlyMode && isTioProvider(mapTileProvider)
    ? 'rainIntensity'
    : 'precipitationIntensity';

  const effectiveTheme = mapThemeOverride ?? theme;

  // -- Refs --
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const tioActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const imageCacheRef = useRef(new LRUImageCache(80));
  const preloaderRef = useRef(new RadarPreloader(imageCacheRef.current, 4));

  // Unique layer IDs for numeric radar (avoid conflict with Nowcasting main map)
  const numericIds = useRef({
    sourceA: `widget-radar-numeric-tiles-${Date.now()}`,
    layerA: `widget-radar-numeric-layer-${Date.now()}`,
    sourceB: `widget-radar-numeric-tiles-2-${Date.now()}`,
    layerB: `widget-radar-numeric-layer-2-${Date.now()}`,
  });

  // -- Map state --
  const [styleVersion, setStyleVersion] = useState(0);

  // -- Data state --
  const [circuitData, setCircuitData] = useState<CircuitWithGeoJSON | null>(null);
  const [tiles, setTiles] = useState<RadarTile[]>([]);
  const [nowcastTiles, setNowcastTiles] = useState<RadarTile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tilesSource, setTilesSource] = useState('prod');
  const [tileUrlModalOpen, setTileUrlModalOpen] = useState(false);

  const eventIsActive = isEventActive(selectedEvent);

  // TIO layer follows the dashboard clock directly (single timestamp bridge for useTioLayer)
  const tioTimestampsForLayer = useMemo(() => {
    if (!isTioProvider(mapTileProvider) || !player?.currentTimestamp) return [];
    const iso = player.currentTimestamp.toISOString().replace(/\.\d{3}Z$/, 'Z');
    return [iso];
  }, [mapTileProvider, player?.currentTimestamp]);

  // Combine realtime + nowcast tiles only when event is active and nowcast is enabled
  const liveEndMinutesForRadar = player?.liveEndMinutesForRadar ?? 30;
  const extendedTiles = useMemo(() => {
    if (!eventIsActive || liveEndMinutesForRadar === 0 || tiles.length === 0) return tiles;

    if (mapTileProvider === 'radar') {
      // Derive MP nowcast URLs from the last realtime tile (same as Nowcasting)
      const lastTile = tiles[tiles.length - 1];
      const derived: RadarTile[] = NOWCAST_OFFSETS.map(offset => ({
        timestamp: lastTile.timestamp,
        url: lastTile.url.replace('.png', `+${String(offset).padStart(2, '0')}.png`),
        datetime: lastTile.datetime.add(offset, 'minute'),
        isNowcast: true,
        forecastOffset: offset,
      }));
      return [...tiles, ...derived];
    }

    // L2 / L2-numeric: use backend-provided nowcast tiles
    if (nowcastTiles.length === 0) return tiles;
    return [...tiles, ...nowcastTiles];
  }, [tiles, nowcastTiles, eventIsActive, liveEndMinutesForRadar, mapTileProvider]);

  // -- Initialize map --
  useMapInitialization({
    selectedEvent,
    mapStyle: mapStyleKey,
    theme: effectiveTheme,
    mapContainerRef,
    mapRef,
    setStyleVersion,
    disableViewPersistence: true,
    initialCenter: savedMapCenter ?? undefined,
    initialZoom: savedMapZoom ?? undefined,
    expandAttributionOnLoad: false,
    showZoomIndicator,
  });

  // Resize map when widget dimensions change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timer = setTimeout(() => map.resize(), 50);
    return () => clearTimeout(timer);
  }, [pixelSize.width, pixelSize.height]);

  // -- Fetch circuit data --
  useEffect(() => {
    if (!selectedEvent) return;
    let cancelled = false;
    circuitsService
      .getCircuitById(selectedEvent.circuit_id, selectedEvent.circuit_geojson_id)
      .then((data) => {
        if (!cancelled) setCircuitData(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.circuit_id, selectedEvent?.circuit_geojson_id]);

  // -- Fetch tiles when date range or provider changes --
  useEffect(() => {
    if (!selectedEvent || !selectedDateRange) {
      setTiles([]);
      setNowcastTiles([]);
      return;
    }

    if (isTioProvider(mapTileProvider)) {
      setTiles([]);
      setNowcastTiles([]);
      setCurrentIndex(0);
      return;
    }

    let cancelled = false;

    const fetchTiles = async () => {
      setLoading(true);
      try {
          const startTs = dayjs(selectedDateRange.start).utc().startOf('minute').format('YYYYMMDDHHmmss');
          const endTs = dayjs(selectedDateRange.end).utc().endOf('minute').format('YYYYMMDDHHmmss');

          const isL2 = mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric';
          const isNumeric = mapTileProvider === 'radar-l2-numeric';
          const data = isNumeric
            ? await radarTilesService.getRadarTilesL2Pngd({ start_timestamp: startTs, end_timestamp: endTs })
            : isL2
              ? await radarTilesService.getRadarTilesL2({ start_timestamp: startTs, end_timestamp: endTs })
              : await radarTilesService.getRadarTiles({
                  season_year: selectedEvent.season_year,
                  circuit_id: selectedEvent.circuit_id,
                  start_date: selectedEvent.start_date,
                  source: 'legacy',
                  start_timestamp: startTs,
                  end_timestamp: endTs,
                });

          if (!cancelled && data.tiles) {
            const allParsed = data.tiles.map(parseApiTile);

            if (isL2) {
              // Separate realtime and nowcast tiles, same as useDataFetching
              const realtime = allParsed.filter(t => !t.isNowcast);
              const nowcast = allParsed.filter(t => t.isNowcast);

              // Only keep nowcast tiles from the latest base timestamp
              if (nowcast.length > 0 && realtime.length > 0) {
                const lastBaseTs = realtime[realtime.length - 1].timestamp;
                setNowcastTiles(nowcast.filter(t => t.timestamp.startsWith(lastBaseTs + '+')));
              } else {
                setNowcastTiles([]);
              }
              setTiles(realtime);
            } else {
              setTiles(allParsed);
              setNowcastTiles([]);
            }

            setCurrentIndex(0);
            setTilesSource(isL2 ? 'l2' : getEventSource(selectedEvent));
          }
      } catch {
        // Silently handle fetch errors
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTiles();
    return () => { cancelled = true; };
  }, [selectedEvent, selectedDateRange, mapTileProvider]);

  const stations = showWindArrows ? sharedStations : [];

  // -- Live tile refresh (5-minute interval for active events) --
  // Use refs to avoid recreating the interval on every playback frame advance
  const extendedTilesRef = useRef(extendedTiles);
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => { extendedTilesRef.current = extendedTiles; }, [extendedTiles]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  useEffect(() => {
    if (!eventIsActive || !selectedEvent || !selectedDateRange) return;
    if (isTioProvider(mapTileProvider)) return;

    const refresh = async () => {
      try {
        const startTs = dayjs(selectedDateRange.start).utc().startOf('minute').format('YYYYMMDDHHmmss');
        const endTs = dayjs(selectedDateRange.end).utc().endOf('minute').format('YYYYMMDDHHmmss');

        const isL2 = mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric';
        const isNumeric = mapTileProvider === 'radar-l2-numeric';
        const data = isNumeric
          ? await radarTilesService.getRadarTilesL2Pngd({ start_timestamp: startTs, end_timestamp: endTs })
          : isL2
            ? await radarTilesService.getRadarTilesL2({ start_timestamp: startTs, end_timestamp: endTs })
            : await radarTilesService.getRadarTiles({
                season_year: selectedEvent.season_year,
                circuit_id: selectedEvent.circuit_id,
                start_date: selectedEvent.start_date,
                source: 'legacy',
                start_timestamp: startTs,
                end_timestamp: endTs,
              });

        if (data.tiles) {
          const allParsed = data.tiles.map(parseApiTile);

          // Capture current playback time to dock after refresh (via refs)
          const currentMs = extendedTilesRef.current[currentIndexRef.current]?.datetime?.valueOf();

          if (isL2) {
            const realtime = allParsed.filter(t => !t.isNowcast);
            const nowcast = allParsed.filter(t => t.isNowcast);
            if (nowcast.length > 0 && realtime.length > 0) {
              const lastBaseTs = realtime[realtime.length - 1].timestamp;
              setNowcastTiles(nowcast.filter(t => t.timestamp.startsWith(lastBaseTs + '+')));
            } else {
              setNowcastTiles([]);
            }
            setTiles(realtime);

            if (currentMs) {
              const newExtended = nowcast.length > 0 && realtime.length > 0
                ? [...realtime, ...nowcast.filter(t => t.timestamp.startsWith(realtime[realtime.length - 1].timestamp + '+'))]
                : realtime;
              const dockIdx = findClosestIndex(newExtended, currentMs);
              setCurrentIndex(dockIdx);
            }
          } else {
            setTiles(allParsed);
            setNowcastTiles([]);
            if (currentMs) {
              const dockIdx = findClosestIndex(allParsed, currentMs);
              setCurrentIndex(dockIdx);
            }
          }
          setTilesSource(isL2 ? 'l2' : getEventSource(selectedEvent));
        }
      } catch {
        // Silently handle refresh errors
      }
    };

    const intervalId = window.setInterval(refresh, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [eventIsActive, selectedEvent, selectedDateRange, mapTileProvider]);

  // Sync radar tile index with player's currentTimestamp
  useEffect(() => {
    if (!player?.currentTimestamp || isTioProvider(mapTileProvider)) return;
    if (extendedTiles.length === 0) return;
    const playerMs = player.currentTimestamp.getTime();

    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < extendedTiles.length; i++) {
      const diff = Math.abs(extendedTiles[i].datetime.valueOf() - playerMs);
      if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
    }
    setCurrentIndex(bestIdx);
  }, [player?.currentTimestamp, extendedTiles, mapTileProvider]);

  // Current timestamp for storm tracking / rain path hooks (YYYYMMDDHHmmss format)
  // Use realtime tiles only; nowcast frames return null to hide observed overlays
  const currentTimestampUtc = useMemo(() => {
    if (isTioProvider(mapTileProvider) && player?.currentTimestamp) {
      return dayjs(player.currentTimestamp).utc().format('YYYYMMDDHHmmss');
    }
    if (extendedTiles.length > 0 && extendedTiles[currentIndex]) {
      const tile = extendedTiles[currentIndex];
      if (tile.isNowcast) return null;
      return tile.datetime.utc().format('YYYYMMDDHHmmss');
    }
    return null;
  }, [mapTileProvider, player?.currentTimestamp, extendedTiles, currentIndex]);

  // Current tile URL for debug modal
  const currentTileUrl = useMemo(() => {
    if (isTioProvider(mapTileProvider)) return null;
    const tile = extendedTiles[currentIndex];
    return tile?.url ?? null;
  }, [mapTileProvider, extendedTiles, currentIndex]);

  const extraOrderLayers = useMemo(() => [
    'rain-path-line', 'rain-path-ticks', 'rain-path-dots', 'rain-path-labels',
    'storm-track-line', 'storm-track-points', 'storm-arrow', 'storm-label',
  ], []);

  const currentFps = player?.playbackSpeed ?? 1;

  // -- Numeric radar layer (decodes .pngd tiles client-side) --
  const { currentBlobUrl: numericBlobUrl } = useNumericRadarLayer({
    mapRef,
    selectedEvent,
    extendedTiles,
    currentIndex,
    radarOpacity,
    fps: currentFps,
    styleVersion,
    mapTileProvider,
    numericNoiseFloor,
    numericColormap,
    radarNumericSourceId: numericIds.current.sourceA,
    radarNumericLayerId: numericIds.current.layerA,
    radarNumericSourceId2: numericIds.current.sourceB,
    radarNumericLayerId2: numericIds.current.layerB,
    imageCacheRef,
  });

  // -- Preload upcoming radar tiles --
  useEffect(() => {
    if (isTioProvider(mapTileProvider) || extendedTiles.length === 0) {
      preloaderRef.current.cancel();
      return;
    }
    preloaderRef.current.resume();
    const tasks = buildPreloadPriorities(extendedTiles, currentIndex);
    preloaderRef.current.schedule(tasks);
  }, [extendedTiles, currentIndex, mapTileProvider]);

  // -- Wire map layers (reuses the same hooks as split/PiP maps) --
  useMapLayers({
    mapRef,
    role: 'main',
    enabled: true,
    styleVersion,
    selectedEvent,
    circuitData,
    trackLineWidth,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    isCircleOverlayVisible: showCircleOverlay,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
    extendedTiles,
    radarOpacity,
    fps: currentFps,
    tilesSource,
    mapTileProvider,
    numericBlobUrl,
    tioTimestamps: tioTimestampsForLayer,
    tioCurrentIndex: 0,
    tioTileField: effectiveTioField,
    tioActiveLayerRef,
    extraOrderLayers,
  });

  useStormTrackingLayer({
    mapRef,
    selectedEvent,
    showStormTracking,
    featureEnabled: isRoot,
    selectedDateRange,
    currentTimestampUtc,
    styleVersion,
  });

  useRainPathLayer({
    mapRef,
    selectedEvent,
    showRainPath,
    featureEnabled: true,
    selectedDateRange,
    currentTimestampUtc,
    styleVersion,
  });

  // -- Toolbar handlers --
  const handleCenterOnCircuit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent) return;
    map.flyTo({
      center: [selectedEvent.circuit.longitude || 0, selectedEvent.circuit.latitude || 0],
      zoom: 13,
      duration: 800,
    });
  }, [selectedEvent]);

  const handleResetToSavedView = useCallback(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent) return;
    const lat = selectedEvent.circuit.latitude || 0;
    const lon = selectedEvent.circuit.longitude || 0;
    const center: [number, number] = savedMapCenter ?? [lon, lat];
    const zoom = savedMapZoom ?? 13;
    map.flyTo({ center, zoom, duration: 800 });
  }, [selectedEvent, savedMapCenter, savedMapZoom]);

  const handleSaveView = useCallback(() => {
    const map = mapRef.current;
    if (!map || !onConfigChange) return;
    const center = map.getCenter();
    const zoom = Math.round(map.getZoom() * 1000) / 1000;
    onConfigChange({
      mapCenter: [
        Math.round(center.lng * 1e6) / 1e6,
        Math.round(center.lat * 1e6) / 1e6,
      ],
      mapZoom: zoom,
    });
    message.success('Map view saved');
  }, [onConfigChange, message]);

  const handleCopyTileUrl = async () => {
    if (!currentTileUrl) return;
    try {
      await navigator.clipboard.writeText(currentTileUrl);
      message.success('URL copied to clipboard');
    } catch {
      message.error('Failed to copy URL');
    }
  };

  // -- Render --
  const showOverlay = !selectedEvent || !selectedDateRange;
  const hasData = isTioProvider(mapTileProvider) ? tioTimestampsForLayer.length > 0 : extendedTiles.length > 0;
  const showNoDataOverlay = !showOverlay && !loading && !hasData;
  const btnStyle = {
    width: 29, height: 29, display: 'flex' as const, alignItems: 'center' as const, justifyContent: 'center' as const,
    padding: 0, border: 'none', backgroundColor: 'transparent', cursor: 'pointer' as const,
  };
  const iconColor = effectiveTheme === 'dark' ? '#fff' : '#333';
  const isDarkBasemap = effectiveTheme === 'dark';

  return (
    <div
      className="radar-map-widget"
      data-basemap-theme={effectiveTheme}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      {/* Always mount the map container so the ref is available for useMapInitialization */}
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

      {showOverlay && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: theme === 'dark' ? '#141414' : '#fff',
        }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 11 }}>
                {!selectedEvent ? 'No event selected' : 'Select a time range on the timeline'}
              </span>
            }
          />
        </div>
      )}

      {showNoDataOverlay && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 10,
          background: theme === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
          borderRadius: 6, padding: '6px 12px',
        }}>
          <span style={{ fontSize: 11, color: theme === 'dark' ? '#faad14' : '#d48806' }}>
            No radar data available for selected range
          </span>
        </div>
      )}

      {loading && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 10,
          background: theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
          borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Spin size="small" />
          <span style={{ fontSize: 11, color: iconColor }}>Loading...</span>
        </div>
      )}

      <div
        className="radar-map-widget-toolbar"
        style={{
          position: 'absolute',
          top: 110,
          right: 0,
          margin: '0 10px 0 0',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 10,
        }}
      >
        <div
          className="mapboxgl-ctrl-group radar-map-widget-toolbar-group"
          style={{
            backgroundColor: isDarkBasemap ? '#1f1f1f' : '#fff',
            boxShadow: isDarkBasemap ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
            borderRadius: 4,
          }}
        >
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleCenterOnCircuit}
            title="Center on Circuit"
            style={{ ...btnStyle, borderBottom: isDarkBasemap ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)' }}
          >
            <EnvironmentOutlined style={{ fontSize: 15, color: iconColor }} />
          </button>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleResetToSavedView}
            title="Reset to Saved View"
            style={{ ...btnStyle, borderBottom: isDarkBasemap ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)' }}
          >
            <AimOutlined style={{ fontSize: 15, color: iconColor }} />
          </button>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleSaveView}
            title="Save Current View"
            style={{ ...btnStyle, borderBottom: isRoot ? (isDarkBasemap ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)') : undefined }}
          >
            <PushpinOutlined style={{ fontSize: 15, color: iconColor }} />
          </button>
          {isRoot && (
            <button
              type="button"
              className="mapboxgl-ctrl-icon"
              onClick={() => setTileUrlModalOpen(true)}
              title="Show current tile URL"
              style={btnStyle}
            >
              <LinkOutlined style={{ fontSize: 15, color: iconColor }} />
            </button>
          )}
        </div>
      </div>

      {showLegend && (
        <div style={{
          position: 'absolute',
          bottom: 30,
          left: 10,
          zIndex: 10,
        }}>
          <RadarLegend
            mapTileProvider={mapTileProvider}
            isRainOnlyMode={isRainOnlyMode}
            numericColormap={numericColormap}
            compact={legendCompact}
            onCompactChange={onConfigChange ? (v) => onConfigChange({ legendCompact: v }) : undefined}
            onClose={onConfigChange ? () => onConfigChange({ showLegend: false }) : undefined}
          />
        </div>
      )}

      <style>{`
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-group {
          background-color: #1f1f1f !important;
          box-shadow: 0 0 0 2px rgba(255,255,255,.1) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-group button {
          background-color: transparent !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-group button:hover {
          background-color: rgba(255,255,255,.1) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-group button + button {
          border-top: 1px solid rgba(255,255,255,.1) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-zoom-in .mapboxgl-ctrl-icon,
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-zoom-out .mapboxgl-ctrl-icon,
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
          filter: invert(1);
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-attrib {
          background-color: rgba(31, 31, 31, 0.8) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-attrib a {
          color: rgba(255, 255, 255, 0.75) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-attrib-inner {
          color: rgba(255, 255, 255, 0.65) !important;
        }
        .radar-map-widget[data-basemap-theme="dark"] .mapboxgl-ctrl-attrib-button {
          filter: invert(1);
        }
        .radar-map-widget .radar-map-widget-toolbar-group {
          float: none;
          margin: 0;
        }
      `}</style>

      {isRoot && (
        <Modal
          title="Current Tile URL"
          open={tileUrlModalOpen}
          onCancel={() => setTileUrlModalOpen(false)}
          footer={[
            <Button key="copy" onClick={handleCopyTileUrl} disabled={!currentTileUrl}>Copy URL</Button>,
            <Button key="close" onClick={() => setTileUrlModalOpen(false)}>Close</Button>,
          ]}
        >
          {currentTileUrl ? (
            <Input.TextArea readOnly value={currentTileUrl} rows={6} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          ) : (
            <p style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.45)' }}>
              No tile currently loaded
            </p>
          )}
        </Modal>
      )}
    </div>
  );
};
